<template>
  <div class="max-w-5xl mx-auto space-y-8">
    <el-form ref="formRef" :model="formData" :rules="rules" label-width="120px" label-position="top">
      <!-- 成果归属 -->
      <AchievementTypeSection
        :achievement-type="formData.achievementType"
        :team-members="formData.teamMembers"
        @update:achievement-type="handleAchievementTypeChange"
        @update:team-members="handleTeamMembersChange"
      />

      <!-- 软件基本信息 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">软件基本信息</h3>
        <div class="space-y-4">
          <el-form-item label="软件名称" prop="software_name" required>
            <el-input
              v-model="formData.software_name"
              placeholder="请输入软件名称"
              class="custom-input"
            />
          </el-form-item>
          <el-form-item label="版本号">
            <el-input
              v-model="formData.version"
              placeholder="如：V1.0"
              class="custom-input"
            />
          </el-form-item>
          <el-form-item label="软件著作权登记号" prop="registration_no" required>
            <el-input
              v-model="formData.registration_no"
              placeholder="请输入软件著作权登记号"
              class="custom-input"
            />
          </el-form-item>
        </div>
      </div>

      <!-- 开发与登记时间 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">开发与登记时间</h3>
        <div class="space-y-4">
          <el-form-item label="开发完成时间" prop="development_time" required>
            <el-date-picker
              v-model="formData.development_time"
              type="date"
              placeholder="选择开发完成时间"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              class="custom-input w-full"
              style="width: 100%"
            />
          </el-form-item>
          <el-form-item label="登记时间">
            <el-date-picker
              v-model="formData.registration_time"
              type="date"
              placeholder="选择登记时间"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              class="custom-input w-full"
              style="width: 100%"
            />
          </el-form-item>
        </div>
      </div>

      <!-- 软件类型 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">软件类型</h3>
        <div class="space-y-4">
          <el-form-item label="软件类型" prop="software_type" required>
            <el-select 
              v-model="formData.software_type" 
              placeholder="请选择软件类型"
              class="custom-select w-full"
              popper-class="custom-select-dropdown"
            >
              <el-option value="system" label="系统软件" />
              <el-option value="application" label="应用软件" />
              <el-option value="middleware" label="中间件" />
              <el-option value="embedded" label="嵌入式软件" />
              <el-option value="tool" label="工具软件" />
              <el-option value="other" label="其他" />
            </el-select>
          </el-form-item>
        </div>
      </div>

      <!-- 其他信息 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">其他信息</h3>
        <div class="space-y-4">
          <el-form-item label="备注">
            <el-input
              v-model="formData.remark"
              type="textarea"
              :rows="3"
              placeholder="请输入备注信息"
              class="custom-input"
            />
          </el-form-item>
        </div>
      </div>

      <!-- 第五组：佐证材料 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">佐证材料</h3>
        <FileUpload
          ref="uploadRef"
          title="佐证材料上传"
          description="请上传软件著作权登记证书或相关证明"
          @success="onUploadSuccess"
          @change="onUploadChange"
        />
      </div>

      <!-- 提交按钮 -->
      <div class="flex justify-end space-x-4 pt-6 border-t border-gray-200">
        <el-button 
          @click="submitForm(true)"
          class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none border bg-white text-gray-900 hover:bg-gray-50 hover:text-gray-900 h-9 px-4 py-2"
        >
          保存草稿
        </el-button>
        <el-button 
          @click="submitForm(false)"
          class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none bg-blue-600 text-white hover:bg-blue-700 h-9 px-4 py-2 border-0"
        >
          提交
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, nextTick } from 'vue'
import FileUpload from '../common/FileUpload.vue'
import AchievementTypeSection from '../common/AchievementTypeSection.vue'
import { saveSoftware, submitSoftware, updateSoftware, getSoftware } from '../../api/software'
import { ElMessage } from 'element-plus'
import { ensureValidTeamMembers } from '../../utils/teamMembers'
import { useRoute } from 'vue-router'
import { getMyInfo } from '../../api/user'

const formData = reactive({
  achievementType: '',
  isFirstAuthor: '',
  isFirstInUnit: '',
  teamMembers: [],
  software_name: '',
  version: '',
  registration_no: '',
  development_time: '',
  registration_time: '',
  software_type: '',
  remark: ''
})

const currentId = ref(null)
const loading = ref(false)
const evidenceMaterial = ref('')
const evidenceMaterialList = ref([])
const myInfo = ref(null)
const formRef = ref(null)
const uploadRef = ref(null)
const LAST_DRAFT_KEY = 'software:lastDraftId'
const DRAFT_FORM_KEY = 'software:draftForm'
const DRAFT_STATUS_KEY = 'software:draftStatus'

const rules = reactive({
  software_name: [{ required: true, message: '请输入软件名称', trigger: 'blur' }],
  registration_no: [{ required: true, message: '请输入软件著作权登记号', trigger: 'blur' }],
  development_time: [{ required: true, message: '请选择开发完成时间', trigger: 'change' }],
  software_type: [{ required: true, message: '请选择软件类型', trigger: 'change' }]
})

function sanitizeEvidenceMaterial(s) {
  const v = String(s || '')
  if (!v) return ''
  return v
    .split(',')
    .map(p => String(p || '').trim().replace(/`/g, ''))
    .filter(Boolean)
    .join(',')
}

function isValidUrl(u) {
  return /^https?:\/\/\S+$/.test(String(u || ''))
}

function filterValidEvidence(s) {
  const cleaned = sanitizeEvidenceMaterial(s)
  if (!cleaned) return ''
  const parts = cleaned.split(',').map(x => x.trim()).filter(Boolean)
  const valid = parts.filter(isValidUrl)
  return valid.join(',')
}

const IDENTITY_LABELS = { teacher: '教师', student: '学生', social: '社会人士' }
function toIdentityLabel(v) {
  const key = String(v || '')
  return IDENTITY_LABELS[key] ?? key
}

function normalizeAchievementType(v) {
  if (v === 'individual') return 1
  if (v === 'team') return 2
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function denormalizeAchievementType(v) {
  if (v === 1 || v === '1') return 'individual'
  if (v === 2 || v === '2') return 'team'
  return ''
}

function normalizeMembers(members = []) {
  return (Array.isArray(members) ? members : []).map((m, idx) => ({
    position: m?.rank != null ? parseInt(m.rank, 10) || idx + 1 : idx + 1,
    name: m?.name || '',
    identity: toIdentityLabel(m?.identity),
    unit: m?.unit || ''
  }))
}

function toDateTime(input) {
  if (!input) return undefined
  const d = typeof input === 'string' ? new Date(input) : input
  if (!(d instanceof Date) || isNaN(d.getTime())) return undefined
  const pad = (n) => String(n).padStart(2, '0')
  const y = d.getFullYear()
  const m = pad(d.getMonth() + 1)
  const day = pad(d.getDate())
  const h = pad(d.getHours())
  const min = pad(d.getMinutes())
  const s = pad(d.getSeconds())
  return `${y}-${m}-${day} ${h}:${min}:${s}`
}

function mapFormToApi(data, isDraft, id) {
  const dev = toDateTime(data.development_time)
  const reg = toDateTime(data.registration_time)
  const dto = {
    id: id != null ? Number(id) : undefined,
    achievementType: normalizeAchievementType(data.achievementType),
    softwareName: data.software_name || '',
    version: data.version || '',
    copyrightRegistrationNumber: data.registration_no || '',
    softwareType: data.software_type || '',
    remark: data.remark || '',
    evidenceMaterial: filterValidEvidence(evidenceMaterial.value || ''),
    status: isDraft ? 1 : 2,
    memberList: normalizeMembers(data.teamMembers)
  }
  if (dev) dto.developmentCompletionTime = dev
  if (reg) dto.registrationTime = reg
  if (dto.achievementType === 1 && (!Array.isArray(dto.memberList) || dto.memberList.length === 0)) {
    const info = myInfo.value || {}
    const name = String(info?.name || '')
    const identity = String(info?.role || '教师')
    const unit = String(info?.college || '')
    dto.memberList = name || unit ? [{ position: 1, name, identity, unit }] : []
  }
  return dto
}

function mapApiToForm(dto = {}) {
  formData.achievementType = denormalizeAchievementType(dto.achievementType)
  formData.teamMembers = Array.isArray(dto.memberList)
    ? dto.memberList.map((m, i) => ({
        id: `${Date.now()}_${i}`,
        name: String(m?.name || ''),
        identity: String(m?.identity || ''),
        unit: String(m?.unit || ''),
        rank: Number(m?.position ?? i + 1) || i + 1
      }))
    : []
  formData.software_name = dto.softwareName ?? ''
  formData.version = dto.version ?? ''
  formData.registration_no = dto.copyrightRegistrationNumber ?? ''
  formData.development_time = dto.developmentCompletionTime ? new Date(dto.developmentCompletionTime) : ''
  formData.registration_time = dto.registrationTime ? new Date(dto.registrationTime) : ''
  formData.software_type = dto.softwareType ?? ''
  formData.remark = dto.remark ?? ''
  const em = dto.evidenceMaterial || ''
  evidenceMaterial.value = em
  evidenceMaterialList.value = em ? String(em).split(',').filter(Boolean) : []
}
const handleAchievementTypeChange = (value) => {
  formData.achievementType = value
}


const handleTeamMembersChange = (members) => {
  formData.teamMembers = members
}

function onUploadSuccess({ url }) {
  if (url && !evidenceMaterialList.value.includes(url)) {
    evidenceMaterialList.value.push(url)
    evidenceMaterial.value = evidenceMaterialList.value.join(',')
  }
}

function onUploadChange({ url, type }) {
  if (type === 'remove' && url) {
    evidenceMaterialList.value = evidenceMaterialList.value.filter(u => u !== url)
    evidenceMaterial.value = evidenceMaterialList.value.join(',')
  }
}

const submitForm = async (isDraft = false) => {
  try {
    loading.value = true
    const ok = await (formRef.value?.validate?.() ?? Promise.resolve(true)).catch(() => false)
    if (!ok) {
      ElMessage.error('请完善所有必填项')
      loading.value = false
      return
    }
    if (!formData.achievementType) {
      ElMessage.error('请选择成果归属')
      loading.value = false
      return
    }
    if (formData.achievementType === 'team') {
      const { ok: membersOk, message } = ensureValidTeamMembers(formData.achievementType, formData.teamMembers)
      if (!membersOk) {
        ElMessage.error(message || '团队成员信息未填写完整')
        loading.value = false
        return
      }
    }
    const base = mapFormToApi(formData, isDraft, currentId.value)
    if (!currentId.value) {
      if (isDraft) {
        const resp = await saveSoftware(base)
        const id = resp?.id ?? resp?.data?.id ?? resp?.data?.data?.id
        if (id) currentId.value = id
      } else {
        const resp = await submitSoftware(base)
        const id = resp?.id ?? resp?.data?.id ?? resp?.data?.data?.id
        if (id) currentId.value = id
      }
    } else {
      const updateDto = { ...base, id: Number(currentId.value), status: isDraft ? 1 : 2 }
      await updateSoftware(updateDto)
    }
    if (isDraft) {
      if (!currentId.value) {
        try {
          localStorage.setItem(LAST_DRAFT_KEY, String(currentId.value))
          localStorage.setItem(DRAFT_STATUS_KEY, '1')
          const snapshot = {
            form: { ...formData },
            evidenceMaterial: evidenceMaterial.value || '',
            evidenceMaterialList: Array.isArray(evidenceMaterialList.value) ? [...evidenceMaterialList.value] : [],
            currentId: currentId.value || null
          }
          localStorage.setItem(DRAFT_FORM_KEY, JSON.stringify(snapshot))
        } catch (e) { console.warn(e) }
      }
    } else {
      try { await nextTick(); formRef.value?.clearValidate?.(); formRef.value?.resetFields?.() } catch (e) { console.warn(e) }
      currentId.value = null
      try { localStorage.removeItem(LAST_DRAFT_KEY) } catch (e) { console.warn(e) }
      try { localStorage.removeItem(DRAFT_FORM_KEY) } catch (e) { console.warn(e) }
      try { localStorage.removeItem(DRAFT_STATUS_KEY) } catch (e) { console.warn(e) }
      try { resetForm() } catch (e) { console.warn(e) }
    }
    ElMessage.success(isDraft ? '草稿保存成功' : '提交成功')
  } catch {
    ElMessage.error(isDraft ? '草稿保存失败' : '提交失败')
  } finally {
    loading.value = false
  }
}

const route = useRoute()
onMounted(async () => {
  try {
    const info = await getMyInfo()
    const data = info?.data ?? info
    myInfo.value = data
  } catch (e) { console.warn(e) }
  const rid = route.query?.detailId || route.query?.id  // ← 优先用 detailId
  if (rid) {
    try {
      currentId.value = rid
      const dto = await getSoftware(rid)
      const data = dto?.data ?? dto
      mapApiToForm(data)
    } catch (e) { console.warn(e) }
  } else {
    try {
      const draftFlag = localStorage.getItem(DRAFT_STATUS_KEY)
      if (draftFlag === '1') {
        const raw = localStorage.getItem(DRAFT_FORM_KEY)
        if (raw) {
          try {
            const snapshot = JSON.parse(raw)
            const f = snapshot?.form || {}
            Object.assign(formData, f)
            evidenceMaterial.value = snapshot?.evidenceMaterial || ''
            evidenceMaterialList.value = Array.isArray(snapshot?.evidenceMaterialList) ? [...snapshot.evidenceMaterialList] : []
            const id = snapshot?.currentId || localStorage.getItem(LAST_DRAFT_KEY)
            if (id) currentId.value = id
          } catch (e) { console.warn(e) }
        }
      }
    } catch (e) { console.warn(e) }
  }
})
const resetForm = () => {
  Object.assign(formData, {
    achievementType: '',
    isFirstAuthor: '',
    isFirstInUnit: '',
    teamMembers: [],
    software_name: '',
    version: '',
    registration_no: '',
    development_time: '',
    registration_time: '',
    software_type: '',
    remark: ''
  })
  evidenceMaterial.value = ''
  evidenceMaterialList.value = []
  currentId.value = null
  try { uploadRef.value?.clear?.() } catch (e) { console.warn(e) }
}
</script>

<style scoped>
/* 自定义输入框样式 */
.custom-input {
  @apply w-full;
}

.custom-input :deep(.el-input__wrapper) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.custom-input :deep(.el-input__wrapper:hover) {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

.custom-input :deep(.el-input__wrapper.is-focus) {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background-color: white;
}

.custom-input :deep(.el-input__inner) {
  color: #111827;
  background-color: transparent;
}

.custom-input :deep(.el-input__inner::placeholder) {
  color: #9ca3af;
}

.custom-input :deep(.el-textarea__inner) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  color: #111827;
}

.custom-input :deep(.el-textarea__inner:hover) {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

.custom-input :deep(.el-textarea__inner:focus) {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background-color: white;
}

.custom-input :deep(.el-textarea__inner::placeholder) {
  color: #9ca3af;
}

/* 自定义选择框样式 */
.custom-select {
  @apply w-full;
}

.custom-select :deep(.el-select__wrapper) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.custom-select :deep(.el-select__wrapper:hover) {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

.custom-select :deep(.el-select__wrapper.is-focused) {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background-color: white;
}

.custom-select :deep(.el-select__placeholder) {
  color: #9ca3af;
}

.custom-select :deep(.el-select__input) {
  color: #111827;
  background-color: transparent;
}

/* 自定义日期选择器样式 */
.custom-input :deep(.el-date-editor) {
  width: 100%;
}

.custom-input :deep(.el-date-editor .el-input__wrapper) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.custom-input :deep(.el-date-editor .el-input__wrapper:hover) {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

.custom-input :deep(.el-date-editor .el-input__wrapper.is-focus) {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background-color: white;
}

/* 下拉菜单样式 */
.custom-select-dropdown {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  background-color: white;
}

.custom-select-dropdown .el-select-dropdown__item {
  padding: 8px 12px;
  color: #374151;
  transition: all 0.2s ease-in-out;
}

.custom-select-dropdown .el-select-dropdown__item:hover {
  background-color: #f3f4f6;
  color: #111827;
}

.custom-select-dropdown .el-select-dropdown__item.is-selected {
  background-color: #dbeafe;
  color: #1d4ed8;
  font-weight: 500;
}

/* 基础表单布局 */
.el-form-item {
  margin-bottom: 20px;
}

.el-form-item__label {
  color: #374151;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
}

/* 单选按钮样式 - 与成果归属保持一致 */
.scale-\[0\.4\] {
  transform: scale(0.4);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .flex.space-x-4 {
    flex-direction: column;
    gap: 12px;
  }
  
  .flex.space-x-4 > * {
    margin-left: 0 !important;
  }
}
</style>